import React, { useMemo } from 'react';
import { SummaryPanelContainer, SummaryPanelIcon, SummaryPanelRow, useFeatureToggle } from '@css-xp/base';
import capitalize from 'lodash/capitalize';
import unionBy from 'lodash/unionBy';
import get from 'lodash/get';
import defaultTo from 'lodash/defaultTo';
import moment from 'moment';
import Tooltip from '@atlaskit/tooltip';
import InfoIcon from '@atlaskit/icon/glyph/info';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import { Flex } from '@atlaskit/primitives';
import { BillingFrequency } from '@atlassian/cssxp-ccp-types';
import { FormatCurrency, NoData } from '@css-xp/ppc-common';
import { ApolloQueryResult } from '@apollo/client';
import { EntitlementDetails, Invoice } from '@css-xp/graphtypes/src/generated-types-new';
import { isActiveEntitlement, getFormattedDate } from '@css-xp/entitlements-v2';
import { isAnnualTermedSubscription, isLoomProduct } from '@css-xp/ppc-utils';
import { CurrentBillingPeriod } from './current-billing-period';
import { PARTNER_DISCOUNT_TYPE, PARTNER_MARGIN_TYPE, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { NextBillDate } from './next-bill-date';
import { NextInvoiceEstimateTooltip } from './next-invoice-estimate-tooltip';
import './billing-cycle-section.scss';

const getPartnerDiscountPercentage = (entitlement: EntitlementDetails, latestInvoice: Invoice) => {
  const invoiceLineItemsForCurrentEntitlement = latestInvoice?.lineItems?.filter(
    (lineItem) => lineItem?.entitlementId === entitlement.id
  );

  const billingFrequencyToConsider =
    entitlement.scheduledChanges?.pricingPlan?.billingFrequency ?? entitlement?.billingFrequency;

  const relatedLastCyclePartnerDiscountsKey =
    billingFrequencyToConsider === BillingFrequency.ANNUAL ? 'margins' : 'discounts';
  const relatedNextCyclePartnerDiscountsKey =
    billingFrequencyToConsider === BillingFrequency.ANNUAL ? 'margins' : 'adjustments';
  const relatedDiscountType =
    billingFrequencyToConsider === BillingFrequency.ANNUAL ? PARTNER_MARGIN_TYPE : PARTNER_DISCOUNT_TYPE;

  const exactLineItemToExtractLastCycleDiscountPercentage = invoiceLineItemsForCurrentEntitlement?.find(
    (invoiceLineItemForCurrentEntitlement) => {
      return defaultTo(invoiceLineItemForCurrentEntitlement?.[relatedLastCyclePartnerDiscountsKey], []).length > 0;
    }
  );

  const lastCyclePartnerDiscountPercentage = (defaultTo(
    exactLineItemToExtractLastCycleDiscountPercentage?.[relatedLastCyclePartnerDiscountsKey],
    []
  ) as any).find((discount: any) => discount?.type === relatedDiscountType)?.percent;

  // Get all the unique discount percentages from the last invoice
  const discountsPercentage = invoiceLineItemsForCurrentEntitlement?.reduce((acc, currentValue) => {
    currentValue?.[relatedLastCyclePartnerDiscountsKey]?.forEach((discount) => {
      if (discount?.type === relatedDiscountType && discount?.percent) {
        if (!acc.includes(discount.percent)) {
          acc.push(discount.percent);
        }
      }
    });

    return acc;
  }, [] as (number | string)[]);

  const nextCyclePartnerAdjustmentsOrMargins = entitlement?.nextBillEstimate?.lastBillEstimateItem?.[
    relatedNextCyclePartnerDiscountsKey
  ] as Array<{
    type: string;
    amount: number;
    percent: number;
    promotionId: string;
  }>;
  const nextCyclePartnerDiscountOrMargin = nextCyclePartnerAdjustmentsOrMargins?.find(
    (discount) => discount.type === relatedDiscountType
  );

  const nextCyclePartnerDiscountPercentage = nextCyclePartnerDiscountOrMargin?.percent;

  return {
    lastCyclePartnerDiscountPercentage,
    isMultipleLastCycleDiscountPresent: defaultTo(discountsPercentage, []).length > 1,
    nextCyclePartnerDiscountPercentage,
  };
};

export const BillingCycleSection = ({
  entitlement,
  refetch,
}: {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}) => {
  const [isEnabled] = useFeatureToggle();
  const { billingFrequency, nextBillEstimate, createdDate } = entitlement;
  const activePromotions = defaultTo(entitlement?.promotions, []);
  const scheduledPromotions = entitlement?.scheduledChanges?.promotions || [];
  const promotions = unionBy(activePromotions, scheduledPromotions, 'promotionInstanceId')?.length || 0;
  const invoices = get(entitlement, 'invoices.nodes', []);

  const latestInvoice = useMemo(() => {
    return invoices.length > 0
      ? invoices.reduce((acc: { createdDate: string }, currentValue: { createdDate: string }) =>
          moment(currentValue?.createdDate) > moment(acc?.createdDate) ? currentValue : acc
        )
      : null;
  }, [invoices]);

  const isLoomSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_LOOM_SUPPORT);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isFutureDatedEntitlementEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS);
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
  const isGracePeriodVisibilityEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_GRACE_PERIOD_VISIBILITY);

  const shouldDisplayNextInvoiceEstimateTooltip =
    isLoomSupportEnabled &&
    isLoomProduct(entitlement?.productId) &&
    entitlement?.billingFrequency === BillingFrequency.MONTHLY;

  const {
    lastCyclePartnerDiscountPercentage,
    nextCyclePartnerDiscountPercentage,
    isMultipleLastCycleDiscountPresent,
  } = useMemo(() => {
    return getPartnerDiscountPercentage(entitlement, latestInvoice);
  }, [entitlement, latestInvoice]);

  const isActive = isActiveEntitlement(entitlement);
  const isTermedSubscription = isAnnualTermedSubscription(entitlement);

  const shouldDisplayLastInvoiceDiscount = !isMultipleLastCycleDiscountPresent;

  const shouldDisplayNextInvoiceDetails = !isTermedSubscription;
  const shouldDisplayNextInvoiceDiscount = shouldDisplayNextInvoiceDetails;

  const shouldHideLastInvoiceDetails = entitlement?.isExternallyBilled;

  return (
    <SummaryPanelContainer
      title={isDesignIteration1Enabled ? undefined : 'Billing cycle'}
      iconKey={SummaryPanelIcon.BILLING_CYCLE_ICON}
      isViewMoreItemsVisibleByDefault={true}
      version={isDesignIteration1Enabled ? 2 : undefined}
    >
      <SummaryPanelRow
        useWrapper={isDesignIteration1Enabled}
        label="Current billing"
        value={
          <CurrentBillingPeriod
            entitlement={entitlement}
            showFutureBillingPeriod={isFutureDatedEntitlementEnabled}
            isSupportForManualBillingEnabled={true}
          />
        }
      />
      <SummaryPanelRow
        useWrapper={isDesignIteration1Enabled}
        label="Billing cycle"
        value={billingFrequency && isActive ? capitalize(billingFrequency) : <NoData />}
      />
      <SummaryPanelRow
        useWrapper={isDesignIteration1Enabled}
        label="Last invoice date"
        value={
          !shouldHideLastInvoiceDetails && latestInvoice?.createdDate ? (
            getFormattedDate(latestInvoice?.createdDate)
          ) : (
            <NoData />
          )
        }
      />
      <SummaryPanelRow
        useWrapper={isDesignIteration1Enabled}
        label="Last invoice amount"
        value={
          !shouldHideLastInvoiceDetails && latestInvoice ? (
            <FormatCurrency
              value={latestInvoice?.grandTotal}
              currency={isMultiCurrencyChangesEnabled ? latestInvoice?.pricingCurrency : latestInvoice?.paymentCurrency}
              showCurrencyCode
              showCommas
            />
          ) : (
            <NoData />
          )
        }
        isHiddenByDefault
      />
      {shouldDisplayLastInvoiceDiscount && (
        <SummaryPanelRow
          useWrapper={isDesignIteration1Enabled}
          label="Last invoice discount"
          value={
            !shouldHideLastInvoiceDetails && lastCyclePartnerDiscountPercentage ? (
              `${lastCyclePartnerDiscountPercentage}%`
            ) : (
              <NoData />
            )
          }
          isHiddenByDefault
        />
      )}
      {!isDesignIteration1Enabled && shouldDisplayNextInvoiceDetails && (
        <SummaryPanelRow
          useWrapper={isDesignIteration1Enabled}
          label="Next invoice date"
          value={nextBillEstimate ? <NextBillDate entitlement={entitlement} refetch={refetch} /> : <NoData />}
          isHiddenByDefault
        />
      )}
      {!isDesignIteration1Enabled && shouldDisplayNextInvoiceDetails && (
        <SummaryPanelRow
          useWrapper={isDesignIteration1Enabled}
          label={
            <div className="label-with-icon-ppc">
              <label>Next invoice estimate </label>
              {shouldDisplayNextInvoiceEstimateTooltip && (
                <NextInvoiceEstimateTooltip nextBillEstimate={nextBillEstimate}>
                  <InfoIcon size="small" label="Next invoice estimate" testId="next-invoice-estimate-info-icon" />
                </NextInvoiceEstimateTooltip>
              )}
            </div>
          }
          value={
            nextBillEstimate && isActive ? (
              <span>
                <FormatCurrency
                  value={nextBillEstimate?.total ?? 0}
                  currency={nextBillEstimate?.currency}
                  showCurrencyCode
                  showCommas
                />
                {!!nextBillEstimate?.total && ' (excl. taxes)'}
              </span>
            ) : (
              <NoData />
            )
          }
          isHiddenByDefault
        />
      )}
      {shouldDisplayNextInvoiceDiscount && (
        <SummaryPanelRow
          useWrapper={isDesignIteration1Enabled}
          label="Next invoice discount"
          value={nextCyclePartnerDiscountPercentage ? `${nextCyclePartnerDiscountPercentage}%` : <NoData />}
          isHiddenByDefault
        />
      )}
      <SummaryPanelRow
        useWrapper={isDesignIteration1Enabled}
        label={
          <div className="label-with-icon-ppc">
            <label>Discounts </label>
            <Tooltip content="Includes discounts that will be applied from the next bill date" tag="span">
              <QuestionCircleIcon size="small" label="active-promotion-info" />
            </Tooltip>
          </div>
        }
        value={
          isDesignIteration1Enabled ? (
            <Flex gap="space.100" alignItems="center">
              <span>Active: {promotions && promotions > 0 ? <label>{promotions}</label> : <label>0</label>}</span>
              <a href="#discounts">View</a>
            </Flex>
          ) : (
            <a href="#active-discounts">
              Active: {promotions && promotions > 0 ? <label>{promotions}</label> : <label>0</label>}
            </a>
          )
        }
        isHiddenByDefault
      />
      <SummaryPanelRow
        useWrapper={isDesignIteration1Enabled}
        label="Created"
        value={<div>{getFormattedDate(createdDate as string)}</div>}
        isHiddenByDefault
      />
    </SummaryPanelContainer>
  );
};
